// The reservation timeline reuses the established pointer geometry and overlap layout.
import type { TimeBlock } from "@trameria/core/timeblocker-shared";
import { m } from "@trameria/i18n";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiErrorMessage } from "../data/errors";
import { Icon } from "../ui/icon";
import { useCoarsePointer } from "../ui/pointer";
import {
  clock,
  DAY_MINUTES,
  DROP_MINUTES,
  dropStart,
  HOUR_PX,
  isoAt,
  layoutColumns,
  minutesInDay,
  PX_PER_MIN,
  SNAP_MINUTES,
} from "./day";
import { useAddBlock } from "./queries";
import { blockAt, useCarry, useTimeBlockerScreen } from "./screen-state";
import { TimeBlockBox } from "./time-block";

/** The own lane's room at its left for the work bars (4 px and a gap). */
const WORK_LEAD_PX = 6;
/** How near the timeline's top or bottom edge a carried card scrolls it (px). */
const EDGE_PX = 40;

/** Where a thing sits across its lane (its column of the overlaps, after the lead). */
function columnCss(c: { col: number; cols: number }, lead = 0) {
  return {
    left: `calc(${lead}px + (100% - ${lead}px) * ${c.col / c.cols} + 2px)`,
    width: `calc((100% - ${lead}px) / ${c.cols} - 4px)`,
  };
}

export function TimeAxis({
  label,
  placed,
}: {
  label: string;
  placed: { block: TimeBlock }[];
}) {
  const screen = useTimeBlockerScreen();
  const add = useAddBlock();
  const coarse = useCoarsePointer();
  const creating = useRef<{ y: number; at: number } | null>(null);
  const [draft, setDraft] = useState<{ start: number; end: number } | null>(
    null,
  );
  const create = (start: number, end: number) =>
    add.mutate(
      {
        ...screen.place,
        startAt: isoAt(screen.dayStart, start),
        endAt: isoAt(screen.dayStart, end),
      },
      {
        onSuccess: (block) => {
          if (block) screen.setSelected(block.id);
        },
        onError: (e) =>
          screen.setError(apiErrorMessage(e, m.timeblocker_save_failed())),
      },
    );
  const { day, today, now, dayStart } = screen;
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const isToday = day === today;
  const nowMin = isToday ? minutesInDay(now.toISOString(), dayStart) : null;

  // Today: an hour before now at the top; other days 8:00.
  // frontend-development: not a fetch; the scroll position when the day changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the day changes; the clock moving on does not scroll
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = nowMin !== null ? (nowMin - 60) * PX_PER_MIN : 8 * HOUR_PX;
    el.scrollTop = Math.max(0, top);
  }, [day, dayStart]);

  // The carried card asks where it is over the own lane, and scrolls near the edges.
  // frontend-development: not a fetch; hands the timeline's geometry to the list's carrying.
  useEffect(() => {
    const hit = (x: number, y: number) => {
      const grid = gridRef.current?.getBoundingClientRect();
      const box = scrollRef.current?.getBoundingClientRect();
      if (!grid || !box) return null;
      if (x < grid.left || x > grid.right || y < box.top || y > box.bottom)
        return null;
      return dropStart((y - grid.top) / PX_PER_MIN);
    };
    let pointerY: number | null = null;
    let frame = 0;
    const step = () => {
      frame = 0;
      const box = scrollRef.current;
      const carried = screen.carry.get();
      if (!box || !carried || pointerY === null) return;
      const r = box.getBoundingClientRect();
      const speed =
        pointerY < r.top + EDGE_PX && pointerY > r.top - EDGE_PX
          ? -Math.ceil((r.top + EDGE_PX - pointerY) / 4)
          : pointerY > r.bottom - EDGE_PX && pointerY < r.bottom + EDGE_PX
            ? Math.ceil((pointerY - (r.bottom - EDGE_PX)) / 4)
            : 0;
      if (speed === 0) return;
      const before = box.scrollTop;
      box.scrollTop += speed;
      if (box.scrollTop === before) return;
      screen.carry.set({
        ...carried,
        minute: hit(carried.x, carried.y),
        overBlock: blockAt(carried.x, carried.y),
      });
      frame = requestAnimationFrame(step);
    };
    screen.hitMinute.current = hit;
    screen.edgeScroll.current = (y) => {
      pointerY = y;
      if (!frame) frame = requestAnimationFrame(step);
    };
    return () => {
      cancelAnimationFrame(frame);
      screen.hitMinute.current = null;
      screen.edgeScroll.current = null;
    };
  }, [screen.carry, screen.hitMinute, screen.edgeScroll]);

  const layout = layoutColumns(
    placed.map(({ block }) => ({
      key: block.id,
      start: minutesInDay(block.startAt, dayStart),
      end: minutesInDay(block.endAt, dayStart),
    })),
  );
  const hourLines = Array.from({ length: 24 }, (_, h) => (
    <div
      // biome-ignore lint/suspicious/noArrayIndexKey: the fixed lines of 0 to 23 o'clock
      key={h}
      aria-hidden="true"
      className="absolute inset-x-0 border-t border-border-soft"
      style={{ top: h * HOUR_PX }}
    />
  ));
  const nowLine = (dot: boolean) =>
    nowMin !== null && (
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 z-10 border-t border-danger"
        style={{ top: nowMin * PX_PER_MIN }}
      >
        {dot && (
          <span className="absolute -top-[4px] -left-[4px] h-[7px] w-[7px] rounded-full bg-danger" />
        )}
      </div>
    );

  return (
    <section
      aria-label={label}
      className="flex min-h-0 min-w-0 flex-1 flex-col"
    >
      <div
        ref={scrollRef}
        className="panel-scroll min-h-0 flex-1 overflow-y-auto"
      >
        <div
          className="relative flex"
          style={{ height: DAY_MINUTES * PX_PER_MIN }}
        >
          {/* The hours. */}
          <div aria-hidden="true" className="relative w-[52px] flex-none">
            {Array.from({ length: 24 }, (_, h) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: the fixed labels of 0 to 23 o'clock
                key={h}
                className="absolute right-2 -translate-y-1/2 text-[11px] tabular-nums text-faint"
                style={{ top: h * HOUR_PX }}
              >
                {h === 0 ? "" : `${h}:00`}
              </span>
            ))}
          </div>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: positional timeline input; the header's Add button provides keyboard creation. */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: a tap chooses a position; keyboard creation uses the header's Add button. */}
          <div
            ref={gridRef}
            data-tb-own-lane=""
            className={`relative flex-1 border-l border-border-soft mr-3`}
            onClick={(e) => {
              if (!coarse || (e.target as Element).closest("[data-axis-item]"))
                return;
              const at = dropStart(
                (e.clientY - e.currentTarget.getBoundingClientRect().top) /
                  PX_PER_MIN,
              );
              create(at, at + DROP_MINUTES);
            }}
            onDoubleClick={(e) => {
              if (coarse) return;
              if ((e.target as Element).closest("[data-axis-item]")) return;
              const at = dropStart(
                (e.clientY - e.currentTarget.getBoundingClientRect().top) /
                  PX_PER_MIN,
              );
              create(at, at + DROP_MINUTES);
            }}
            onPointerDown={(e) => {
              if (
                e.button !== 0 ||
                (e.target as Element).closest("[data-axis-item]") ||
                e.pointerType === "touch"
              )
                return;
              const at = dropStart(
                (e.clientY - e.currentTarget.getBoundingClientRect().top) /
                  PX_PER_MIN,
              );
              creating.current = { y: e.clientY, at };
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              const o = creating.current;
              if (!o || Math.abs(e.clientY - o.y) < 4) return;
              const at = Math.min(
                DAY_MINUTES,
                Math.max(
                  0,
                  Math.round(
                    (e.clientY - e.currentTarget.getBoundingClientRect().top) /
                      PX_PER_MIN /
                      SNAP_MINUTES,
                  ) * SNAP_MINUTES,
                ),
              );
              setDraft({
                start: Math.min(o.at, at),
                end: Math.max(o.at + SNAP_MINUTES, at),
              });
            }}
            onPointerUp={() => {
              if (creating.current && draft) create(draft.start, draft.end);
              creating.current = null;
              setDraft(null);
            }}
            onPointerCancel={() => {
              creating.current = null;
              setDraft(null);
            }}
          >
            {hourLines}
            <CarryGhost />
            {draft && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-1 rounded border border-dashed border-accent bg-chosen"
                style={{
                  top: draft.start * PX_PER_MIN,
                  height: (draft.end - draft.start) * PX_PER_MIN,
                }}
              />
            )}
            {placed.map(({ block }) => (
              <TimeBlockBox
                key={block.id}
                block={block}
                column={layout.get(block.id) ?? { col: 0, cols: 1 }}
                columnCss={columnCss}
                lead={WORK_LEAD_PX}
              />
            ))}
            {nowLine(true)}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Where a carried card would put its block (30 minutes from the step under the pointer). */
function CarryGhost() {
  const carry = useCarry();
  // Over a block the card goes into it: no new place is drawn.
  if (carry?.minute == null || carry.overBlock) return null;
  const at = carry.minute;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-[2px] rounded-[6px] border border-dashed border-accent bg-chosen px-2 text-[11px] tabular-nums text-chosen-fg"
      style={{ top: at * PX_PER_MIN, height: DROP_MINUTES * PX_PER_MIN }}
    >
      {clock(at)}–{clock(at + DROP_MINUTES)}
    </div>
  );
}

/** The carried card under the pointer (what the HTML5 drag's image was). */
export function CarriedCard() {
  const carry = useCarry();
  if (!carry) return null;
  return (
    // The card as it is in the list, very faint (it only shows where it goes).
    <div
      aria-hidden="true"
      className="pointer-events-none fixed z-50 flex w-[260px] items-center gap-2 rounded-[8px] bg-hover px-2 py-[7px] text-[13.5px] leading-[20px] text-fg opacity-35"
      style={{ left: carry.x + 12, top: carry.y - 16 }}
    >
      <Icon
        kind={carry.item.action.id && !carry.caseOnly ? "action" : "cases"}
        size={16}
        className="flex-none text-muted"
      />
      <span className="min-w-0 truncate">{carry.item.action.title}</span>
    </div>
  );
}
